import { CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchCurrentUser } from "@/api/auth";
import { fetchKpis, fetchTasks, fetchUpcoming, fetchConflicts, DashboardTask, UpcomingBooking } from "@/api/dashboard";
import { Btn } from "@/components/Btn";
import { Card } from "@/components/Card";
import { PageHeader } from "@/components/PageHeader";
import { StatusPill } from "@/components/StatusPill";
import { UnitChip } from "@/components/UnitChip";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

const TASK_ICONS: Record<DashboardTask["key"], { icon: string; color: string }> = {
  neue_anfragen: { icon: "mark_email_unread", color: "var(--status-new)" },
  bestaetigung_offen: { icon: "fact_check", color: "var(--status-confirm)" },
  konus_link_offen: { icon: "link", color: "var(--status-konus-link)" },
  konus_daten_offen: { icon: "edit_note", color: "var(--status-konus-daten)" },
  infos_gruppe_offen: { icon: "info", color: "var(--status-info)" },
  abreise_pruefen: { icon: "flight_takeoff", color: "var(--status-done)" },
  anzahlung_offen: { icon: "payments", color: "var(--task-bill)" },
  storno_rechnung_offen: { icon: "receipt_long", color: "var(--status-bill)" },
  rechnung_offen: { icon: "receipt_long", color: "var(--status-bill)" },
  rechnung_eingang_offen: { icon: "mark_email_read", color: "var(--status-closed)" },
};

const MONTH_DE = ["Jan", "Feb", "Mrz", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

export function DashboardPage() {
  useDocumentTitle("Dashboard");
  const navigate = useNavigate();
  const meQuery = useQuery({ queryKey: ["me"], queryFn: fetchCurrentUser, retry: false });
  const kpisQuery = useQuery({ queryKey: ["dashboard", "kpis"], queryFn: fetchKpis, retry: false });
  const tasksQuery = useQuery({ queryKey: ["dashboard", "tasks"], queryFn: fetchTasks, retry: false });
  const upcomingQuery = useQuery({
    queryKey: ["dashboard", "upcoming"],
    queryFn: () => fetchUpcoming(5),
    retry: false,
  });
  const conflictsQuery = useQuery({
    queryKey: ["dashboard", "conflicts"],
    queryFn: fetchConflicts,
    retry: false,
  });

  const displayName = (meQuery.data?.full_name || meQuery.data?.username || "…").trim() || "…";
  const today = new Date();
  const dateLabel = today.toLocaleDateString("de-DE", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  const kpis = kpisQuery.data;
  const tasks = tasksQuery.data ?? [];
  const openInquiries = kpis?.open_inquiries ?? 0;
  const openTasks = tasks.reduce((sum, t) => sum + (t.count > 0 ? 1 : 0), 0);
  const conflicts = conflictsQuery.data;
  const conflictCount = conflicts?.count ?? 0;

  return (
    <div>
      <PageHeader
        eyebrow={dateLabel}
        title={`Guten Tag, ${displayName}`}
        subtitle={`${openInquiries} offene Anfragen · ${openTasks} Aufgabenkategorien mit offenen Einträgen.`}
        actions={
          <>
            <Btn variant="secondary" icon="insights" onClick={() => navigate("/statistik")}>
              Statistik
            </Btn>
            <Btn variant="primary" icon="add" onClick={() => navigate("/bookings/new")}>
              Neue Buchung
            </Btn>
          </>
        }
      />

      {conflictCount > 0 && (
        <ConflictBanner
          count={conflictCount}
          einheit={conflicts?.first_einheit ?? null}
          zeitraum={conflicts?.first_zeitraum ?? null}
          onClick={() => navigate("/bookings?tab=konflikt")}
        />
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 20 }}>
        <Kpi label="Buchungen gesamt" value={kpis ? String(kpis.total_bookings) : "…"} />
        <Kpi label={`Buchungen ${today.getFullYear()}`} value={kpis ? String(kpis.bookings_current_year) : "…"} />
        <Kpi label="Offene Anfragen" value={kpis ? String(kpis.open_inquiries) : "…"} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, alignItems: "start", marginBottom: 16 }}>
        <TaskSection
          title="Buchungsstelle"
          tasks={tasks.filter((t) => t.section === "buchung")}
          isLoading={tasksQuery.isLoading}
          isError={tasksQuery.isError}
          onNavigate={(target) => {
            if (target.startsWith("/app/")) navigate(target.slice("/app".length));
            else window.location.assign(target);
          }}
        />
        <TaskSection
          title="Rechnungsstelle"
          tasks={tasks.filter((t) => t.section === "rechnung")}
          isLoading={tasksQuery.isLoading}
          isError={tasksQuery.isError}
          onNavigate={(target) => {
            if (target.startsWith("/app/")) navigate(target.slice("/app".length));
            else window.location.assign(target);
          }}
        />
      </div>

      <UpcomingList query={upcomingQuery} />
    </div>
  );
}

function ConflictBanner({
  count,
  einheit,
  zeitraum,
  onClick,
}: {
  count: number;
  einheit: string | null;
  zeitraum: string | null;
  onClick: () => void;
}) {
  const titel =
    count === 1
      ? "1 überlappende Buchung erkannt"
      : `${count} überlappende Buchungen erkannt`;
  const metaParts = [einheit, zeitraum].filter((x): x is string => !!x);
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        width: "100%",
        padding: "14px 18px",
        marginBottom: 16,
        background: "var(--warn-bg)",
        border: "1px solid var(--warn-border)",
        borderRadius: 10,
        cursor: "pointer",
        fontFamily: "var(--font-body)",
        textAlign: "left",
        color: "var(--warn-fg)",
      }}
    >
      <span
        className="material-icons-outlined"
        style={{ fontSize: 22, color: "var(--warn-icon)", flexShrink: 0 }}
      >
        warning_amber
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--warn-fg)" }}>{titel}</div>
        {metaParts.length > 0 && (
          <div style={{ fontSize: 12, color: "var(--fg-muted)", marginTop: 2 }}>
            {metaParts.join(" · ")}
          </div>
        )}
      </div>
      <span
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: "var(--warn-fg)",
          whiteSpace: "nowrap",
        }}
      >
        Konflikte prüfen →
      </span>
    </button>
  );
}

function TaskSection({
  title,
  tasks,
  isLoading,
  isError,
  onNavigate,
}: {
  title: string;
  tasks: DashboardTask[];
  isLoading: boolean;
  isError: boolean;
  onNavigate: (target: string) => void;
}) {
  return (
    <Card padded={false}>
      <div
        style={{
          padding: "14px 18px",
          borderBottom: "1px solid var(--border)",
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 600 }}>{title}</div>
        <div style={{ fontSize: 11, color: "var(--fg-muted)", marginTop: 2 }}>
          Klick öffnet die Buchungsübersicht mit passendem Filter.
        </div>
      </div>
      {isLoading && <div style={{ padding: "18px", fontSize: 13, color: "var(--fg-muted)" }}>Lade…</div>}
      {isError && (
        <div style={{ padding: "18px", fontSize: 13, color: "var(--status-cancel)" }}>
          Konnte Aufgaben nicht laden.
        </div>
      )}
      {tasks.map((t) => {
        const { icon, color } = TASK_ICONS[t.key];
        return (
          <TaskRow
            key={t.key}
            icon={icon}
            color={color}
            title={t.title}
            meta={t.subtitle}
            count={t.count}
            onClick={() => onNavigate(t.target)}
          />
        );
      })}
    </Card>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <div
        style={{
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: ".08em",
          color: "var(--fg-subtle)",
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 6 }}>
        <div className="display" style={{ fontSize: 32, color: "var(--fg)", fontWeight: 400 }}>
          {value}
        </div>
      </div>
    </Card>
  );
}

type TaskRowProps = {
  icon: string;
  color: string;
  title: string;
  meta: string | null;
  count: number;
  onClick?: () => void;
};

function TaskRow({ icon, color, title, meta, count, onClick }: TaskRowProps) {
  const disabled = count === 0;
  const rowStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 14,
    padding: "12px 14px",
    width: "100%",
    background: "transparent",
    border: "none",
    borderBottom: "1px solid var(--border)",
    cursor: disabled ? "default" : "pointer",
    fontFamily: "var(--font-body)",
    textAlign: "left",
    opacity: disabled ? 0.5 : 1,
    transition: "background .12s",
  };
  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={rowStyle}
      onMouseEnter={(e) => {
        if (!disabled) e.currentTarget.style.background = "var(--bg-hover)";
      }}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          flexShrink: 0,
          background: `color-mix(in oklab, ${color} 16%, var(--bg-card))`,
          color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span className="material-icons-outlined" style={{ fontSize: 20 }}>
          {icon}
        </span>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--fg)" }}>{title}</div>
        {meta && <div style={{ fontSize: 11, color: "var(--fg-muted)", marginTop: 2 }}>{meta}</div>}
      </div>
      <div
        className="display"
        style={{
          fontSize: 22,
          color: disabled ? "var(--fg-subtle)" : color,
          fontWeight: 400,
          minWidth: 28,
          textAlign: "right",
        }}
      >
        {count}
      </div>
      {!disabled && (
        <span className="material-icons-outlined" style={{ fontSize: 18, color: "var(--fg-subtle)" }}>
          chevron_right
        </span>
      )}
    </button>
  );
}

function UpcomingList({ query }: { query: ReturnType<typeof useQuery<UpcomingBooking[], Error>> }) {
  const navigate = useNavigate();
  return (
    <Card padded={false}>
      <div
        style={{
          padding: "14px 18px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid var(--border)",
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 600 }}>Anstehende Anreisen</div>
        <button
          onClick={() => navigate("/bookings")}
          style={{
            fontSize: 12,
            color: "var(--link)",
            fontWeight: 600,
            fontFamily: "var(--font-body)",
            textDecoration: "none",
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 0,
          }}
        >
          Alle ansehen →
        </button>
      </div>
      {query.isLoading && <div style={{ padding: "18px", fontSize: 13, color: "var(--fg-muted)" }}>Lade…</div>}
      {query.isError && (
        <div style={{ padding: "18px", fontSize: 13, color: "var(--status-cancel)" }}>
          Konnte Buchungen nicht laden.
        </div>
      )}
      {(query.data ?? []).length === 0 && !query.isLoading && !query.isError && (
        <div style={{ padding: "18px", fontSize: 13, color: "var(--fg-muted)" }}>Keine Anreisen in den nächsten Tagen.</div>
      )}
      {(query.data ?? []).map((b) => {
        const checkIn = new Date(b.check_in);
        const day = checkIn.getDate();
        const monthLabel = MONTH_DE[checkIn.getMonth()] ?? "";
        const units = b.einheiten_namen;
        return (
          <button
            key={b.id}
            onClick={() => navigate(`/bookings/${b.id}`)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              padding: "12px 18px",
              width: "100%",
              borderBottom: "1px solid var(--border)",
              cursor: "pointer",
              textDecoration: "none",
              color: "inherit",
              background: "transparent",
              border: "none",
              borderBottomWidth: 1,
              borderBottomStyle: "solid",
              borderBottomColor: "var(--border)",
              fontFamily: "var(--font-body)",
              textAlign: "left",
              transition: "background .12s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "var(--bg-hover)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 10,
                flexShrink: 0,
                background: "var(--brand-100)",
                color: "var(--brand-900)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                lineHeight: 1,
              }}
            >
              <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", opacity: 0.7 }}>{monthLabel}</div>
              <div style={{ fontSize: 17, fontWeight: 700 }}>{day}</div>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "var(--fg)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {b.gruppenname || b.gastname || "—"}
              </div>
              <div style={{ fontSize: 11, color: "var(--fg-muted)", marginTop: 2 }}>
                {b.naechte} Nächte · {b.personen_gesamt} Personen
              </div>
            </div>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", justifyContent: "flex-end", maxWidth: 160 }}>
              {units.map((u) => <UnitChip key={u} name={u} />)}
            </div>
            <StatusPill status={b.status} />
          </button>
        );
      })}
    </Card>
  );
}
